// src/components/InstallationSelector.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import "./InstallationSelector.css";

/**
 * Full-page component for selecting an installation within an organization.
 * Handles 0/1/multiple installations with smart navigation logic.
 */
function InstallationSelector({
  organization,
  installations,
  onSelectInstallation,
  onBack,
  onCancel,
  onCreateInstallation,
  onManageInstallations,
  userRoles,
}) {
  const { logout } = useAuth();
  const [copiedId, setCopiedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Helper to check role (supports both string and object formats)
  const hasRole = (roleName) =>
    userRoles?.some(
      (role) => (typeof role === "string" ? role : role?.name) === roleName
    );

  // ESC key handler - go back to app if available and installations exist
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && onCancel && installations.length > 0) {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [onCancel, installations]);

  const handleCopyId = (e, installationId) => {
    e.stopPropagation(); // Prevent triggering the parent button click
    navigator.clipboard.writeText(installationId);
    setCopiedId(installationId);
    setTimeout(() => setCopiedId(null), 2000); // Reset after 2 seconds
  };
  if (installations.length === 0) {
    return (
      <div className="installation-selector-container">
        <div className="installation-selector-card">
          <h2>No Sites Found</h2>
          <p className="error-message">
            The organization "{organization.name}" does not have any
            installations.
          </p>
          <p className="error-message">
            Please{" "}
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (onCreateInstallation) {
                  onCreateInstallation();
                }
              }}
              className="inline-link-button"
              style={{
                background: "none",
                border: "none",
                color: "var(--accent-color)",
                textDecoration: "underline",
                cursor: "pointer",
                padding: 0,
                font: "inherit",
              }}
            >
              click here
            </button>{" "}
            to create a site.
          </p>
          <div className="button-group">
            {onManageInstallations &&
              (hasRole("Full Admin") || hasRole("Organization Admin")) && (
                <button
                  className="manage-button"
                  onClick={onManageInstallations}
                >
                  Go To Manage Sites
                </button>
              )}
            {onBack && (
              <button className="back-button" onClick={onBack}>
                Go to Change Organizations
              </button>
            )}
            {onCancel && (
              <button className="back-button" onClick={onCancel}>
                Back to App
              </button>
            )}
          </div>
          <button className="logout-button" onClick={logout}>
            Logout
          </button>
        </div>
      </div>
    );
  }

  // Filter installations based on search term
  const filteredInstallations = installations.filter((installation) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      installation.name.toLowerCase().includes(search) ||
      installation.installationId.toLowerCase().includes(search)
    );
  });

  return (
    <div className="installation-selector-container">
      <div className="installation-selector-card">
        <h2>Select Site</h2>
        <p className="subtitle">
          Organization: <strong>{organization.name}</strong>
        </p>

        {/* Search Box */}
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search sites by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="search-clear-button"
              onClick={() => setSearchTerm("")}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="installation-list">
          {[...filteredInstallations]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((installation) => (
              <div
                key={installation.installationId}
                className="installation-item"
                onClick={() => onSelectInstallation(installation)}
              >
                <div className="installation-name">{installation.name}</div>
                <div className="installation-id-row">
                  <div className="installation-id">
                    ID: {installation.installationId}
                  </div>
                  <button
                    className="copy-id-button"
                    onClick={(e) =>
                      handleCopyId(e, installation.installationId)
                    }
                    title="Copy Site ID"
                  >
                    {copiedId === installation.installationId ? "✓" : "📋"}
                  </button>
                </div>
              </div>
            ))}
        </div>

        <div className="button-group">
          {onManageInstallations &&
            (hasRole("Full Admin") || hasRole("Organization Admin")) && (
              <button className="manage-button" onClick={onManageInstallations}>
                Go To Manage Sites
              </button>
            )}
          {onBack && (
            <button className="back-button" onClick={onBack}>
              Go to Change Organizations
            </button>
          )}
          {onCancel && (
            <button className="back-button" onClick={onCancel}>
              Back to App
            </button>
          )}
          {!onCancel && !onBack && (
            <button className="logout-button" onClick={logout}>
              Logout
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InstallationSelector;
